/* eslint-disable no-return-assign */
/* eslint-disable react/sort-comp */
/* eslint-disable react/destructuring-assignment */
import { PureComponent, ReactElement } from 'react';
import './SwipeableListItem.scss';

const DragDirection = {
  UP: 1,
  DOWN: 2,
  LEFT: 3,
  RIGHT: 4,
  UNKNOWN: 5,
};

const FPS_INTERVAL = 1000 / 60;

type SwipeActionType = {
  action?: () => void;
  content: ReactElement;
};

type Props = {
  blockSwipe?: boolean;
  children?: ReactElement;
  swipeLeft?: SwipeActionType;
  swipeRight?: SwipeActionType;
  scrollStartThreshold?: number;
  swipeStartThreshold?: number;
  threshold?: number;

  onSwipeEnd?: () => void;
  onSwipeProgress?: (value: number) => void;
  onSwipeStart?: () => void;
};

class SwipeableListItem extends PureComponent<Props> {
  private contentLeft: HTMLElement | null = null;

  private contentRight: HTMLElement | null = null;

  private listElement: HTMLElement | null = null;

  private requestedAnimationFrame: number | null = null;

  private wrapper: HTMLElement | null = null;

  private startTime: number | null = null;

  private previousSwipeDistancePercent = 0;

  private dragStartPoint = { x: -1, y: -1 };

  private dragDirection = DragDirection.UNKNOWN;

  private swipeDirection = DragDirection.UNKNOWN;

  private left = 0;

  private contentLeftWidth = 0;

  private startX = 0;

  constructor(props: Props) {
    super(props);

    this.contentLeft = null;
    this.contentRight = null;
    this.listElement = null;
    this.requestedAnimationFrame = null;
    this.startTime = null;

    this.resetState();
  }

  resetState = (): void => {
    this.dragStartPoint = { x: -1, y: -1 };
    this.dragDirection = DragDirection.UNKNOWN;
    this.left = 0;
    this.previousSwipeDistancePercent = 0;
  };

  get dragHorizontalDirectionThreshold(): number {
    return this.props.swipeStartThreshold || 10;
  }

  get dragVerticalDirectionThreshold(): number {
    return this.props.scrollStartThreshold || 10;
  }

  componentDidMount(): void {
    if (this.contentLeft || this.contentRight) {
      (this.wrapper as HTMLElement).addEventListener('mousedown', this.handleDragStartMouse);

      (this.wrapper as HTMLElement).addEventListener('touchstart', this.handleDragStartTouch);
      (this.wrapper as HTMLElement).addEventListener('touchend', this.handleDragEndTouch);
      (this.wrapper as HTMLElement).addEventListener('touchmove', this.handleTouchMove, {
        capture: true,
        passive: false,
      });

      (this.wrapper as HTMLElement)?.addEventListener('click', this.handleDisableClick);
    }
  }

  componentWillUnmount(): void {
    if (this.requestedAnimationFrame) {
      cancelAnimationFrame(this.requestedAnimationFrame);

      this.requestedAnimationFrame = null;
    }

    (this.wrapper as HTMLElement).removeEventListener('mousedown', this.handleDragStartMouse);

    (this.wrapper as HTMLElement).removeEventListener('touchstart', this.handleDragStartTouch);
    (this.wrapper as HTMLElement).removeEventListener('touchend', this.handleDragEndTouch);
    (this.wrapper as HTMLElement).removeEventListener('touchmove', this.handleTouchMove, {
      capture: true,
      // passive: false,
    });

    (this.wrapper as HTMLElement)?.removeEventListener('click', this.handleDisableClick);
  }

  handleDisableClick = (event: MouseEvent): void => {
    const isContain = this.listElement?.contains(event.target as Node);
    const isDragEvent = (this.left < 0 && this.dragStartedWithinItem())
    || (this.left === 0 && !this.dragStartedWithinItem());

    if (isContain && isDragEvent) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  handleDragStartMouse = (event: MouseEvent): void => {
    window.addEventListener('mouseup', this.handleDragEndMouse);
    window.addEventListener('mousemove', this.handleMouseMove);

    (this.wrapper as HTMLElement).addEventListener('mouseup', this.handleDragEndMouse);
    (this.wrapper as HTMLElement).addEventListener('mousemove', this.handleMouseMove);

    this.handleDragStart(event);
  };

  handleDragStartTouch = (event: TouchEvent): void => {
    window.addEventListener('touchend', this.handleDragEndTouch);

    const touch = event.targetTouches[0];

    this.handleDragStart(touch);
  };

  handleDragStart = ({ clientX, clientY }: { clientX: number; clientY: number }): void => {
    this.startX = clientX;
    // this.resetState();
    this.contentLeftWidth = (this.contentLeft as HTMLElement).children[0].clientWidth;
    this.dragStartPoint = { x: this.dragStartedWithinItem() ? clientX + this.contentLeftWidth : clientX, y: clientY };

    (this.listElement as HTMLElement).className = 'content';
    if (this.contentLeft !== null) {
      this.contentLeft.className = 'content-left';
    }

    if (this.contentRight !== null) {
      this.contentRight.className = 'content-right';
    }

    this.startTime = Date.now();
    this.scheduleUpdatePosition();
  };

  handleMouseMove = (event: MouseEvent): void => {
    if (this.dragStartedWithinItem()) {
      const { clientX, clientY } = event;

      this.setDragDirection(clientX, clientY, this.startX);

      if (this.isSwiping()) {
        event.stopPropagation();
        event.preventDefault();

        this.left = clientX - this.dragStartPoint.x;
        this.scheduleUpdatePosition();
      }
    }
  };

  handleTouchMove = (event: TouchEvent): void => {
    if (this.dragStartedWithinItem()) {
      const { clientX, clientY } = event.targetTouches[0];

      this.setDragDirection(clientX, clientY, this.startX);

      if (!event.cancelable) {
        return;
      }

      if (this.isSwiping()) {
        event.stopPropagation();
        event.preventDefault();

        this.left = clientX - this.dragStartPoint.x;
        this.scheduleUpdatePosition();
      }
    }
  };

  handleDragEndMouse = (): void => {
    window.removeEventListener('mouseup', this.handleDragEndMouse);
    window.removeEventListener('mousemove', this.handleMouseMove);

    if (this.wrapper) {
      this.wrapper.removeEventListener('mouseup', this.handleDragEndMouse);
      this.wrapper.removeEventListener('mousemove', this.handleMouseMove);
    }

    this.handleDragEnd();
  };

  handleDragEndTouch = (): void => {
    window.removeEventListener('touchend', this.handleDragEndTouch);

    this.handleDragEnd();
  };

  handleDragEnd = (): void => {
    if (this.isSwiping()) {
      const threshold = this.props.threshold || 0.5;

      if (this.listElement) {
        if (this.left < this.listElement.offsetWidth * threshold * -1) {
          this.handleSwipedLeft();
        } else if (this.left > this.listElement.offsetWidth * threshold) {
          this.handleSwipedRight();
        }
      }

      if (this.props.onSwipeEnd) {
        this.props.onSwipeEnd();
      }
    }

    if (
      (this.swipeDirection === DragDirection.LEFT && this.previousSwipeDistancePercent < 90)
            || (this.swipeDirection === DragDirection.RIGHT && this.previousSwipeDistancePercent <= 10)
    ) {
      this.resetState();
    } else if (
      (this.swipeDirection === DragDirection.RIGHT && this.previousSwipeDistancePercent > 10)
            || (this.swipeDirection === DragDirection.LEFT && this.previousSwipeDistancePercent >= 90)
    ) {
      this.dragDirection = DragDirection.UNKNOWN;
      // this.dragStartPoint = {x: -1, y: -1};
      this.left = -this.contentLeftWidth;
      this.previousSwipeDistancePercent = 0;
    }

    this.swipeDirection = DragDirection.UNKNOWN;

    if (this.listElement) {
      this.listElement.className = 'content-return';
      this.listElement.style.transform = `translateX(${this.left}px)`;
    }

    // hide backgrounds
    if (this.contentLeft !== null) {
      // this.contentLeft.style.opacity = '0';
      this.contentLeft.className = 'content-left-return';
    }

    if (this.contentRight !== null) {
      // this.contentRight.style.opacity = '0';
      this.contentRight.className = 'content-right-return';
    }
  };

  dragStartedWithinItem = (): boolean => {
    const { x, y } = this.dragStartPoint;

    return x !== -1 && y !== -1;
  };

  setDragDirection = (x: number, y: number, startX: number): void => {
    if (startX && this.swipeDirection === DragDirection.UNKNOWN) {
      const differentX = startX - x;

      // eslint-disable-next-line no-nested-ternary
      this.swipeDirection = differentX < 0
        ? DragDirection.LEFT
        : differentX > 0 ? DragDirection.RIGHT : DragDirection.UNKNOWN;
    }

    if (this.dragDirection === DragDirection.UNKNOWN) {
      const { x: startX, y: startY } = this.dragStartPoint;
      const horizontalDistance = Math.abs(x - startX);
      const verticalDistance = Math.abs(y - startY);

      if (
        horizontalDistance <= this.dragHorizontalDirectionThreshold
                && verticalDistance <= this.dragVerticalDirectionThreshold
      ) {
        return;
      }

      const angle = Math.atan2(y - startY, x - startX);
      const octant = Math.round((8 * angle) / (2 * Math.PI) + 8) % 8;

      // eslint-disable-next-line default-case
      switch (octant) {
        case 0:
          if (this.contentRight !== null && horizontalDistance > this.dragHorizontalDirectionThreshold) {
            this.dragDirection = DragDirection.RIGHT;
          }

          break;
        case 1:
        case 2:
        case 3:
          if (verticalDistance > this.dragVerticalDirectionThreshold) {
            this.dragDirection = DragDirection.DOWN;
          }

          break;
        case 4:
          if (this.contentLeft !== null && horizontalDistance > this.dragHorizontalDirectionThreshold) {
            this.dragDirection = DragDirection.LEFT;
          }
          break;
        case 5:
        case 6:
        case 7:
          if (verticalDistance > this.dragVerticalDirectionThreshold) {
            this.dragDirection = DragDirection.UP;
          }

          break;
      }

      if (this.props.onSwipeStart && this.isSwiping()) {
        this.props.onSwipeStart();
      }
    }
  };

  isSwiping = (): boolean => {
    const { blockSwipe } = this.props;
    const horizontalDrag = this.dragDirection === DragDirection.LEFT || this.dragDirection === DragDirection.RIGHT;

    return !blockSwipe && this.dragStartedWithinItem() && horizontalDrag;
  };

  scheduleUpdatePosition = (): void => {
    if (this.requestedAnimationFrame) {
      return;
    }

    this.requestedAnimationFrame = requestAnimationFrame(() => {
      this.requestedAnimationFrame = null;

      this.updatePosition();
    });
  };

  get onlyLeftContent(): boolean {
    return this.contentLeft !== null && this.contentRight === null;
  }

  get onlyRightContent(): boolean {
    return this.contentLeft === null && this.contentRight !== null;
  }

  updatePosition = (): void => {
    const now = Date.now();
    const elapsed = now - (this.startTime as number);

    if (elapsed > FPS_INTERVAL && this.isSwiping()) {
      let contentToShow = this.left < 0 ? this.contentLeft : this.contentRight;

      if (this.onlyLeftContent && this.left > 0) {
        this.left = 0;
        contentToShow = this.contentLeft;
      }

      if (this.onlyRightContent && this.left < 0) {
        this.left = 0;
        contentToShow = this.contentRight;
      }

      if (!contentToShow) {
        return;
      }

      if (this.listElement) {
        this.listElement.style.transform = `translateX(${this.left}px)`;
      }

      // const opacity = (Math.abs(this.left) / 100).toFixed(2);

      // if (this.props.onSwipeProgress && this.listElement) {
      //     const listElementWidth = this.listElement.offsetWidth;
      //     let swipeDistancePercent = this.previousSwipeDistancePercent;
      //
      //     if (listElementWidth !== 0) {
      //         const swipeDistance = Math.max(0, listElementWidth - Math.abs(this.left));
      //
      //         swipeDistancePercent = 100 - Math.round((100 * swipeDistance) / listElementWidth);
      //     }
      //
      //     if (this.previousSwipeDistancePercent !== swipeDistancePercent) {
      //         this.props.onSwipeProgress(swipeDistancePercent);
      //         this.previousSwipeDistancePercent = swipeDistancePercent;
      //     }
      // }

      if (this.contentLeft) {
        const listElementWidth = (this.contentLeft.children[0] as HTMLElement).offsetWidth;
        let swipeDistancePercent = this.previousSwipeDistancePercent;

        if (listElementWidth !== 0) {
          const swipeDistance = Math.max(0, listElementWidth - Math.abs(this.left));

          swipeDistancePercent = 100 - Math.round((100 * swipeDistance) / listElementWidth);
        }

        if (this.previousSwipeDistancePercent !== swipeDistancePercent) {
          this.previousSwipeDistancePercent = swipeDistancePercent;
        }
      }

      this.startTime = Date.now();
    }
  };

  handleSwipedLeft = (): void => {
    const { swipeLeft: { action = null } = {} } = this.props;

    if (action) {
      action();
    }
  };

  handleSwipedRight = (): void => {
    const { swipeRight: { action = null } = {} } = this.props;

    if (action) {
      action();
    }
  };

  bindContentLeft = (ref: HTMLElement | null): HTMLElement | null => (this.contentLeft = ref);

  bindContentRight = (ref: HTMLElement | null): HTMLElement | null => (this.contentRight = ref);

  bindListElement = (ref: HTMLElement | null): HTMLElement | null => (this.listElement = ref);

  bindWrapper = (ref: HTMLElement | null): HTMLElement | null => (this.wrapper = ref);

  render(): JSX.Element {
    const { children, swipeLeft, swipeRight } = this.props;

    return (
      <div className="swipeable-list-item" ref={this.bindWrapper}>
        {swipeLeft && (
          <div ref={this.bindContentLeft} className="content-left" data-testid="swipe-left-content">
            {swipeLeft.content}
          </div>
        )}
        {swipeRight && (
          <div ref={this.bindContentRight} className="content-right" data-testid="swipe-right-content">
            {swipeRight.content}
          </div>
        )}
        <div ref={this.bindListElement} className="content" data-testid="content">
          {children}
        </div>
      </div>
    );
  }
}

export default SwipeableListItem;
